import { combineLatest } from 'rxjs';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  Input,
  OnInit,
} from '@angular/core';

// Services
import { environment } from '@environments/environment';
import { PL_ROUTING, FeatureFlagName, FeatureFlags } from '@common/constant';

import { Store } from '@ngrx/store';
import {
  PL_API_CONFIG,
  PlAPIConfig,
  selectCurrentUser,
  selectIsPaid,
  User,
  UserGroupNames,
} from 'pl-core-services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InternalAdminService } from '@app/common/services';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';
import { HttpClient } from '@angular/common/http';
import packageJson from '../../../../package.json';

interface NavItem {
  label: string;
  href?: string;
  routerLink?: string;
  icon?: string;
  target?: string;
  requiredGroups?: UserGroupNames[];
  allowedGroups?: UserGroupNames[];
  featureFlag?: FeatureFlagName;
}

const ALL_NAV_ITEMS: NavItem[] = [
  {
    label: 'Home',
    icon: 'home',
    routerLink: `/${PL_ROUTING.LANDING_HOME}`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Student Management',
    icon: 'supervisor_account',
    routerLink: `/${PL_ROUTING.CLIENTS}/management`,
    featureFlag: FeatureFlags.STUDENT_MANAGEMENT,
    allowedGroups: [UserGroupNames.SCHOOL_STAFF_ADMINS],
  },
  {
    label: 'User Management',
    icon: 'manage_accounts',
    routerLink: `/${PL_ROUTING.USER_MANAGEMENT}`,
    allowedGroups: [
      UserGroupNames.SCHOOL_STAFF_ADMINS,
      UserGroupNames.SERVICE_AND_SUPPORT,
      UserGroupNames.ACCOUNT_MANAGERS,
    ],
  },
  {
    label: 'Usage Reports',
    icon: 'assessment',
    routerLink: `/${PL_ROUTING.REPORTS}`,
    allowedGroups: [
      UserGroupNames.SCHOOL_STAFF_ADMINS,
      UserGroupNames.SERVICE_AND_SUPPORT,
      UserGroupNames.ACCOUNT_MANAGERS,
    ],
  },
  {
    label: 'Caseload',
    icon: 'supervisor_account',
    routerLink: `/${PL_ROUTING.CLIENTS}/list`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Schedule',
    icon: 'calendar_month',
    routerLink: `/${PL_ROUTING.CALENDAR}`,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Library',
    icon: 'local_library',
    target: '_blank',
    href: environment.apps.library,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
  {
    label: 'Room',
    icon: 'desktop_windows',
    target: '_blank',
    href: environment.apps.room,
    requiredGroups: [UserGroupNames.SAAS_PROVIDER],
  },
];

@UntilDestroy()
@Component({
  selector: 'pl-sidenav',
  templateUrl: './pl-sidenav.component.html',
  styleUrls: ['./pl-sidenav.component.scss'],
})
export class PlSidenavComponent implements OnInit {
  navItems: NavItem[] = [];

  isPaid = false;

  readonly lms_url = `${environment.login_url}/saml/sso/learnupon?sp=${environment.learnupon_url}&RelayState=Dashboard`;

  private currentUser: User;

  version = packageJson.kangaVersion;

  isInternalAdmin = false;

  constructor(
    @Inject(PL_API_CONFIG) private config: PlAPIConfig,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private internalAdminService: InternalAdminService,
    private featureFlagService: FeatureFlagService,
    private http: HttpClient
  ) {}

  @Input()
  @HostBinding('class.compact')
  public forceCompact: boolean = false;

  ngOnInit() {
    this.store.select(selectCurrentUser).subscribe((user: any) => {
      this.currentUser = user;
    });
    combineLatest([
      this.store.select(selectCurrentUser),
      this.featureFlagService.getAllFeatures(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([user, featureFlags]) => {
        this.isInternalAdmin = this.internalAdminService.isInternalAdmin(user);
        this.navItems = ALL_NAV_ITEMS.filter((item) => {
          if (item.featureFlag && !featureFlags[item.featureFlag]) {
            return false;
          }

          if (item.allowedGroups?.length) {
            return !!item.allowedGroups.find((group) =>
              user?.groups?.includes(group)
            );
          }
          return item.requiredGroups?.every((group) =>
            user?.groups?.includes(group)
          );
        });
        this.cdr.detectChanges();
      });

    this.store
      .select(selectIsPaid)
      .pipe(untilDestroyed(this))
      .subscribe((isPaid) => {
        this.isPaid = isPaid;
      });
  }

  openSupportChat() {
    const zenDeskwidget = (window as any).zE;
    this.http
      .get(`${this.config.gateway}/auth/token/zendesk`)
      .subscribe((resp: any) => {
        if (resp.exists_in_zendesk) {
          zenDeskwidget(
            'messenger',
            'loginUser',
            (callback: (arg: any) => void) => {
              callback(resp.jwt);
            }
          );
          zenDeskwidget('messenger:set', 'conversationFields', [
            { id: '30009313905171', value: this.currentUser.email },
            {
              id: '26287517467923',
              value: `${this.currentUser.first_name} ${this.currentUser.last_name}`,
            },
          ]);
        }
        zenDeskwidget('messenger', 'show');
        zenDeskwidget('messenger', 'open');
      });
  }
}
